'use client'

import { AlertTriangle } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { SettingsCard } from '@/components/dialog/settings/settings-card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import CreditHistoryCard from '@/features/settings/billing/credits/credit-history-card'
import CreditLimitsCard from '@/features/settings/billing/credits/credit-limits-card'
import CreditOverviewCard from '@/features/settings/billing/credits/credit-overview-card'
import CreditUsageCard from '@/features/settings/billing/credits/credit-usage-card'
import { useCreditCheckoutReturn } from '@/features/settings/billing/credits/use-credit-checkout-return'
import { useApiBilling } from '@/service/api-billing.hook'

/**
 * Data API credits (ENG-1094). Its own query and failure state: if billing for credits can't
 * load, the subscription cards above keep working.
 */
export default function CreditsSection() {
  const t = useTranslations('settings.billing.credits')
  const { data: billing, isLoading, isError, refetch, isFetching } = useApiBilling()
  // Mounted before the data lands so a Stripe return is picked up on first render.
  const { processing } = useCreditCheckoutReturn()

  return (
    // Its own block under the plan's sections (the section above draws the hairline).
    <section className="flex flex-col pt-6">
      <h2 className="text-foreground/50 pb-4 text-xs font-medium tracking-wider uppercase">
        {t('sectionTitle')}
      </h2>
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : isError || !billing ? (
        <SettingsCard layout="row">
          <span className="text-foreground/60 flex items-center gap-2 text-sm">
            <AlertTriangle className="text-ox-gold size-4 shrink-0" />
            {t('unavailable')}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {t('retry')}
          </Button>
        </SettingsCard>
      ) : (
        <>
          <CreditOverviewCard billing={billing} processing={processing} />
          <CreditLimitsCard billing={billing} />
          <CreditUsageCard />
          <CreditHistoryCard balance={billing.balance} />
        </>
      )}
    </section>
  )
}
